import React, { useEffect, useState } from "react";
import { transportistasService } from "../services/api";
import { Card, Table, Form, Button, Pagination, InputGroup, Badge, Modal } from "react-bootstrap";
import { FaSearch, FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const PAGE_SIZE = 8;

const TransportistasList = () => {
  const [transportistas, setTransportistas] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [orderBy, setOrderBy] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, pages: 1 });
  const [confirmDelete, setConfirmDelete] = useState({ show: false, id: null, name: '' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchTransportistas();
    // eslint-disable-next-line
  }, [page, orderBy]);

  const fetchTransportistas = async () => {
    setLoading(true);
    try {
      const res = await transportistasService.buscar({
        search,
        page,
        limit: PAGE_SIZE,
        orderBy,
      });
      setTransportistas(res.data.transportistas);
      if (res.data.pagination) {
        setPagination(res.data.pagination);
      } else {
        setPagination((prev) => ({ ...prev, total: res.data.transportistas.length, pages: 1 }));
      }
    } catch (err) {
      setTransportistas([]);
      setPagination({ page: 1, limit: PAGE_SIZE, total: 0, pages: 1 });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchTransportistas();
  };

  const handleOrder = (colIdx) => {
    setOrderBy(colIdx);
    setPage(1);
  };

  return (
    <Card className="mt-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h4>Transportistas</h4>
        <Button
          variant="success"
          className="d-flex align-items-center gap-2"
          onClick={() => navigate('/transportista-detail')}
        >
          <FaPlus />
          Añadir transportista
        </Button>
      </Card.Header>
      <Card.Body>
        <Form onSubmit={handleSearch} className="mb-3">
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Buscar por nombre, apellidos, teléfono o DNI..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button type="submit" variant="primary">
              <FaSearch />
            </Button>
          </InputGroup>
        </Form>

        {/* Desktop */}
        <div className="d-none d-md-block">
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th onClick={() => handleOrder(3)} style={{ cursor: "pointer" }}>TRANSPORTISTA</th>
                <th onClick={() => handleOrder(5)} style={{ cursor: "pointer" }}>CÓDIGO</th>
                <th onClick={() => handleOrder(7)} style={{ cursor: "pointer" }}>TELÉFONO</th>
                <th onClick={() => handleOrder(11)} style={{ cursor: "pointer" }}>DNI</th>
                <th>Externo</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {transportistas.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center">No hay transportistas</td>
                </tr>
              )}
              {transportistas.map((t) => (
                <tr key={t.ID}>
                  <td>{t.TRANSPORTISTA}</td>
                  <td>{t.COD}</td>
                  <td>{t.TELEFONO}</td>
                  <td>{t.DNI}</td>
                  <td>
                    <Badge bg={t.ES_EXTERNO ? "secondary" : "primary"}>
                      {t.ES_EXTERNO ? "Externo" : "Interno"}
                    </Badge>
                  </td>
                  <td style={{ width: 130 }}>
                    <div className="d-flex gap-2">
                      <Button size="sm" variant="outline-primary" title="Editar" onClick={() => navigate(`/transportista-detail?transportista_id=${t.ID}`)}>
                        <FaEdit />
                      </Button>
                      <Button size="sm" variant="outline-danger" title="Eliminar" onClick={() => setConfirmDelete({ show: true, id: t.ID, name: t.TRANSPORTISTA })}>
                        <FaTrash />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        {/* Mobile */}
        <div className="d-md-none">
          {transportistas.length === 0 ? (
            <div className="text-center py-4">No hay transportistas</div>
          ) : (
            <div className="gastos-cards">
              {transportistas.map((t) => (
                <Card key={t.ID} className="mb-3">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="flex-grow-1">
                        <h6 className="mb-1">{t.TRANSPORTISTA}</h6>
                        <small className="text-muted">ID: {t.ID}</small>
                      </div>
                    </div>
                    <div className="row g-2 mb-2">
                      <div className="col-6">
                        <small className="text-muted d-block">Código:</small>
                        <span>{t.COD}</span>
                      </div>
                      <div className="col-6">
                        <small className="text-muted d-block">DNI:</small>
                        <span>{t.DNI}</span>
                      </div>
                    </div>
                    <div className="row g-2 mb-2">
                      <div className="col-12">
                        <small className="text-muted d-block">Teléfono:</small>
                        <span>{t.TELEFONO}</span>
                      </div>
                    </div>
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      <Button variant="outline-primary" size="sm" onClick={() => navigate(`/transportista-detail?transportista_id=${t.ID}`)} title="Editar">
                        <FaEdit />
                      </Button>
                      <Button variant="outline-danger" size="sm" onClick={() => setConfirmDelete({ show: true, id: t.ID, name: t.TRANSPORTISTA })} title="Eliminar">
                        <FaTrash />
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </div>

        {pagination.pages > 1 && (
          <div className="d-flex justify-content-center mt-3">
            <Pagination>
              <Pagination.First onClick={() => setPage(1)} disabled={pagination.page === 1} />
              <Pagination.Prev onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={pagination.page === 1} />
              {Array.from({ length: pagination.pages }, (_, i) => i + 1)
                .filter((pageNum) => pageNum === 1 || pageNum === pagination.pages || (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2))
                .map((pageNum, idx, arr) => {
                  if (idx > 0 && pageNum - arr[idx - 1] > 1) {
                    return [
                      <Pagination.Ellipsis key={`ellipsis-${pageNum}`} disabled />, 
                      <Pagination.Item key={pageNum} active={pageNum === pagination.page} onClick={() => setPage(pageNum)}>{pageNum}</Pagination.Item>
                    ];
                  }
                  return (
                    <Pagination.Item key={pageNum} active={pageNum === pagination.page} onClick={() => setPage(pageNum)}>
                      {pageNum}
                    </Pagination.Item>
                  );
                })}
              <Pagination.Next onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))} disabled={pagination.page === pagination.pages} />
              <Pagination.Last onClick={() => setPage(pagination.pages)} disabled={pagination.page === pagination.pages} />
            </Pagination>
          </div>
        )}

        <Modal show={confirmDelete.show} onHide={() => setConfirmDelete({ show: false, id: null, name: '' })} centered>
          <Modal.Header closeButton>
            <Modal.Title>Eliminar transportista</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            ¿Seguro que deseas eliminar a "{confirmDelete.name}"? Esta acción marcará el registro como inactivo.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setConfirmDelete({ show: false, id: null, name: '' })}>Cancelar</Button>
            <Button
              variant="danger"
              onClick={async () => {
                try {
                  await transportistasService.delete(confirmDelete.id);
                  setConfirmDelete({ show: false, id: null, name: '' });
                  fetchTransportistas();
                } catch (e) {
                  setConfirmDelete({ show: false, id: null, name: '' });
                }
              }}
            >
              Eliminar
            </Button>
          </Modal.Footer>
        </Modal>
      </Card.Body>
    </Card>
  );
};

export default TransportistasList;


